/** Answer formatting utilities — renders human-readable answer summaries for review screens. */
import { type ReactNode } from 'react';
import { Check, X, Info, CircleAlert } from 'lucide-react';
import type { StudySessionStep } from '@formency/shared';
import type { AnsweredStep } from './types';
import type { GradingOutcome } from './sessionUtils';

// ─── Config shapes (from step snapshot JSON) ────────────────────────────────

export interface ConfigOption {
  id: string;
  text?: string;
  label?: string;
}

export interface ConfigCategory {
  id: string;
  label: string;
}

export interface ConfigItem {
  id: string;
  text: string;
}

const COLORS = {
  successText: 'var(--color-success-text, var(--f-success-text))',
  warningText: 'var(--color-warning-text, var(--f-warning-text))',
  errorText: 'var(--color-error-text, var(--f-error-text))',
  infoText: 'var(--color-info-text, var(--f-info-text))',
  infoSubtle: 'var(--color-info-subtle, var(--f-info-subtle))',
  textSecondary: 'var(--color-text-secondary, var(--f-fg-secondary))',
  textMuted: 'var(--color-text-muted, var(--f-fg-muted))',
  successSubtle: 'var(--color-success-subtle, var(--f-success-subtle))',
  warningSubtle: 'var(--color-warning-subtle, var(--f-warning-subtle))',
  errorSubtle: 'var(--color-error-subtle, var(--f-error-subtle))',
} as const;

// ─── Step type label ────────────────────────────────────────────────────────

export function stepTypeLabel(type: string | null): string {
  switch (type) {
    case 'mcq':
      return 'Multiple choice';
    case 'true_false':
      return 'True / False';
    case 'text':
      return 'Text';
    case 'cloze':
      return 'Fill in the blanks';
    case 'ranking':
      return 'Ranking';
    case 'classify':
      return 'Classify';
    case 'cloze_diagram':
      return 'Diagram Blanks';
    case 'cloze_image':
      return 'Label Image';
    case 'image_hotspot':
      return 'Image Hotspot';
    case 'numeric':
      return 'Numeric';
    case 'info':
      return 'Info';
    default:
      return type || 'Unknown';
  }
}

// ─── Answer formatting ──────────────────────────────────────────────────────

export function formatAnswer(step: StudySessionStep, answered: AnsweredStep): ReactNode {
  const { answer, result } = answered;
  const config = step.stepSnapshotPublicJson?.config;

  switch (answer.type) {
    case 'info':
      return null;

    case 'text':
      return answer.value || '—';

    case 'true_false':
      return answer.selected === true ? 'True' : answer.selected === false ? 'False' : '—';

    case 'mcq': {
      const options = (config?.options || []) as ConfigOption[];
      const labels = answer.selectedIds.map((id) => {
        const opt = options.find((o) => o.id === id);
        return opt?.text || opt?.label || id;
      });
      return labels.join(', ') || '—';
    }

    case 'ranking': {
      const options = (config?.options || []) as ConfigOption[];
      return (
        <ol className="list-decimal list-inside space-y-0.5">
          {answer.order.map((id, i) => {
            const opt = options.find((o) => o.id === id);
            const isCorrect = result.rankingResults?.[i]?.isCorrect;
            return (
              <li
                key={id}
                style={{
                  color:
                    isCorrect === true
                      ? COLORS.successText
                      : isCorrect === false
                        ? COLORS.errorText
                        : COLORS.textSecondary,
                }}
              >
                {opt?.text || opt?.label || id}
              </li>
            );
          })}
        </ol>
      );
    }

    case 'cloze': {
      if (!result.blankResults) return '—';
      const entries = Object.entries(answer.blanks);
      return (
        <span>
          {entries.map(([blankId, value], i) => {
            const blankResult = result.blankResults?.[blankId];
            const isCorrect = blankResult?.isCorrect;
            return (
              <span key={blankId}>
                {i > 0 && ', '}
                <span
                  style={{
                    color: isCorrect ? COLORS.successText : COLORS.errorText,
                    fontWeight: 500,
                  }}
                >
                  {value || '___'}
                </span>
                {!isCorrect && blankResult?.correctAnswer && (
                  <span style={{ color: COLORS.textMuted }}> → {blankResult.correctAnswer}</span>
                )}
              </span>
            );
          })}
        </span>
      );
    }

    case 'classify': {
      const categories = (config?.categories || []) as ConfigCategory[];
      const items = (config?.items || []) as ConfigItem[];
      const categoryMap = new Map(categories.map((c) => [c.id, c.label]));

      const grouped = new Map<string, { text: string; itemId: string }[]>();
      for (const cat of categories) grouped.set(cat.id, []);
      for (const item of items) {
        const assignedCat = answer.assignments[item.id];
        if (assignedCat && grouped.has(assignedCat)) {
          grouped.get(assignedCat)!.push({ text: item.text, itemId: item.id });
        }
      }

      const classifyResultMap = new Map(result.classifyResults?.map((r) => [r.itemId, r]) || []);

      return (
        <div className="space-y-1">
          {[...grouped.entries()].map(([catId, catItems]) => {
            if (!catItems.length) return null;
            return (
              <div key={catId}>
                <span style={{ color: COLORS.textMuted, fontWeight: 600 }}>
                  {categoryMap.get(catId) || catId}:
                </span>{' '}
                {catItems.map((ci, i) => {
                  const r = classifyResultMap.get(ci.itemId);
                  return (
                    <span key={ci.itemId}>
                      <span
                        style={{
                          color: r?.isCorrect ? COLORS.successText : COLORS.errorText,
                        }}
                      >
                        {ci.text}
                      </span>
                      {i < catItems.length - 1 && ', '}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }

    case 'cloze_diagram': {
      if (!result.blankResults) return '—';
      const entries = Object.entries(answer.blanks);
      return (
        <span>
          {entries.map(([blankId, value], i) => {
            const blankResult = result.blankResults?.[blankId];
            const isCorrect = blankResult?.isCorrect;
            return (
              <span key={blankId}>
                {i > 0 && ', '}
                <span
                  style={{
                    color: isCorrect ? COLORS.successText : COLORS.errorText,
                    fontWeight: 500,
                  }}
                >
                  {value || '___'}
                </span>
                {!isCorrect && blankResult?.correctAnswer && (
                  <span style={{ color: COLORS.textMuted }}> → {blankResult.correctAnswer}</span>
                )}
              </span>
            );
          })}
        </span>
      );
    }

    case 'cloze_image': {
      if (!result.blankResults) return '—';
      const entries = Object.entries(answer.blanks);
      return (
        <span>
          {entries.map(([blankId, value], i) => {
            const blankResult = result.blankResults?.[blankId];
            const isCorrect = blankResult?.isCorrect;
            return (
              <span key={blankId}>
                {i > 0 && ', '}
                <span
                  style={{
                    color: isCorrect ? COLORS.successText : COLORS.errorText,
                    fontWeight: 500,
                  }}
                >
                  {value || '___'}
                </span>
                {!isCorrect && blankResult?.correctAnswer && (
                  <span style={{ color: COLORS.textMuted }}> → {blankResult.correctAnswer}</span>
                )}
              </span>
            );
          })}
        </span>
      );
    }

    case 'image_hotspot': {
      if (answer.x === null || answer.y === null) return '—';
      const hotspotResult = result.hotspotResult;
      if (hotspotResult?.clickedRegionId) {
        const region = hotspotResult.regions.find((r) => r.id === hotspotResult.clickedRegionId);
        return region?.label || 'Region selected';
      }
      return 'No region hit';
    }

    case 'numeric':
      if (answer.value === null) return '—';
      return `${answer.value}${answer.unit ? ' ' + answer.unit : ''}`;
  }
}

// ─── Result badge ───────────────────────────────────────────────────────────

const BADGE_CONFIG: Record<
  GradingOutcome,
  { bg: string; color: string; Icon: typeof Check; label: string; strokeWidth?: number }
> = {
  correct: {
    bg: COLORS.successSubtle,
    color: COLORS.successText,
    Icon: Check,
    label: 'Correct',
    strokeWidth: 3,
  },
  partial: {
    bg: COLORS.warningSubtle,
    color: COLORS.warningText,
    Icon: CircleAlert,
    label: 'Partially Correct',
  },
  incorrect: {
    bg: COLORS.errorSubtle,
    color: COLORS.errorText,
    Icon: X,
    label: 'Incorrect',
    strokeWidth: 3,
  },
};

export function ResultBadge({ isInfo, outcome }: { isInfo: boolean; outcome?: GradingOutcome }) {
  if (isInfo) {
    return (
      <span
        className="flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium"
        style={{
          fontSize: 'var(--f-text-xs)',
          background: COLORS.infoSubtle,
          color: COLORS.infoText,
        }}
      >
        <Info size={12} />
        Info
      </span>
    );
  }

  if (!outcome) {
    return <span style={{ fontSize: 'var(--f-text-xs)', color: COLORS.textMuted }}>—</span>;
  }

  const { bg, color, Icon, label, strokeWidth } = BADGE_CONFIG[outcome];
  return (
    <span
      className="flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium"
      style={{ fontSize: 'var(--f-text-xs)', background: bg, color }}
    >
      <Icon size={12} {...(strokeWidth ? { strokeWidth } : {})} />
      {label}
    </span>
  );
}
